import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { isNotEmpty, isEmail, maxLength, minLength } from 'class-validator';

export const acceptedMimeTypes =
  '.pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type ValidationFn = (value?: string) => string | true;

export class FormValidator {
  public static all =
    (...validationFns: ValidationFn[]) =>
    (value?: string) => {
      const errors = validationFns.map(fn => fn(value));

      return errors.find(e => e !== true) || true;
    };

  public static isNotEmpty: ValidationFn = value => (isNotEmpty(value) ? true : 'Påkrevd');

  public static isValidEmail = (value?: string) => (isEmail(value) ? true : 'Ugyldig e-post');

  public static isValidDate = (value?: string) =>
    value?.match(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/) ? true : 'Ugyldig dato';

  public static isValidPhoneNumber = (value?: string) => {
    if (!value) {
      return 'Ugyldig telefonnummer';
    }

    try {
      parsePhoneNumberWithError(value, 'NO');
    } catch {
      return 'Ugyldig telefonnummer';
    }

    return isValidPhoneNumber(value, 'NO') ? true : 'Ugyldig telefonnummer';
  };

  public static maxLength = (length: number) => (value?: string) => {
    if (value !== undefined && value !== null) {
      return maxLength(value, length) ? true : `Exceeds max. length allowed (${length})`;
    }

    return true;
  };

  public static minLength = (length: number) => (value?: string) =>
    minLength(value, length) ? true : `Minimum lengde er ${length} karakterer`;

  public static isValidFile = (file?: File | string) => {
    if (!file || typeof file === 'string') {
      return true;
    }

    if (!acceptedMimeTypes.includes(file.type)) {
      return 'Invalid file type.';
    }

    const maxAllowedSize = 5 * 1024 * 1024;

    if (file.size > maxAllowedSize) {
      return 'File must be smaller than 5MB';
    }

    return true;
  };

  public static isValidLoginUsername = (value?: string) =>
    // validate depending if it's email or phone number
    value?.includes('@') ? this.isValidEmail(value) : this.isValidPhoneNumber(value);

  public static isValidPostalCode = (value?: string) =>
    value?.match(/^[A-Za-z0-9]{1,10}$/) ? true : 'Ugyldig postnummer';

  public static isValidStreetNumber = (value?: string) => (value?.match(/^[0-9]*$/) ? true : 'Ugyldig gatenummer');
}
